import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

/**
 * Public view hides what a carrier should not put on a shared screen: its own
 * book (premium, policies, portfolio impact) and bureau-licensed detail such as
 * class tables and form changes, which regulators publish but bureaus license.
 * The public record itself - the circular, the decision, the source documents -
 * stays visible, which is the whole point of the mode.
 *
 * **This is a presentation switch, not access control.** The data has already
 * been sent to this browser, and the API decides who may receive what. Anyone
 * signed in can turn it off, so never treat it as a permission boundary.
 *
 * The choice is remembered per browser, so a screen-share stays safe across a
 * reload. It is deliberately not saved to the account: it belongs to the screen
 * someone is presenting from, not to the person.
 */

export type ViewMode = 'carrier' | 'public'

const KEY = 'ix-view-mode'

interface ViewModeValue {
  mode: ViewMode
  setMode: (mode: ViewMode) => void
  /** True when carrier-confidential and bureau-licensed detail must stay hidden. */
  isPublic: boolean
}

const ViewModeContext = createContext<ViewModeValue>({
  mode: 'carrier',
  setMode: () => undefined,
  isPublic: false,
})

function read(): ViewMode {
  try {
    return window.localStorage.getItem(KEY) === 'public' ? 'public' : 'carrier'
  } catch {
    // Private windows and blocked site data: the carrier view is the default.
    return 'carrier'
  }
}

export function ViewModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ViewMode>(read)

  useEffect(() => {
    try {
      window.localStorage.setItem(KEY, mode)
    } catch {
      /* nothing to do: the mode still applies for this session */
    }
  }, [mode])

  const setMode = useCallback((next: ViewMode) => setModeState(next), [])
  const value = useMemo(() => ({ mode, setMode, isPublic: mode === 'public' }), [mode, setMode])

  return <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>
}

export function useViewMode(): ViewModeValue {
  return useContext(ViewModeContext)
}

/** The top bar's segmented control. */
export function ViewModeToggle() {
  const { mode, setMode } = useViewMode()
  return (
    <div className="ix-viewmode" role="group" aria-label="What this screen shows">
      <button
        type="button"
        className={mode === 'public' ? 'ix-viewmode__btn is-on' : 'ix-viewmode__btn'}
        aria-pressed={mode === 'public'}
        onClick={() => setMode('public')}
        title="Hide this carrier's own book and bureau-licensed detail"
      >
        Public view
      </button>
      <button
        type="button"
        className={mode === 'carrier' ? 'ix-viewmode__btn is-on' : 'ix-viewmode__btn'}
        aria-pressed={mode === 'carrier'}
        onClick={() => setMode('carrier')}
        title="Show everything this organization can see"
      >
        Carrier instance
      </button>
    </div>
  )
}

/**
 * What stands in for something Public view hides. It says which mode would show
 * it, so nobody mistakes a hidden figure for a missing one.
 */
export function HiddenInPublicView({ what }: { what: string }) {
  return (
    <p className="ix-hidden-public">
      {what} is hidden in Public view. Switch to Carrier instance to see it.
    </p>
  )
}
